<script lang="ts" setup>
import { Ref, ref, watch, onMounted } from 'vue';

import { useRealtimeReportStore } from '@/stores';
import { SelectOptionsService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import { RealtimeReportFilter, SelectOptionsServiceGetQueuesResult } from '@/types';

import { FilterIcon } from '@heroicons/vue/solid';

const filterOpened = ref(false);
const realtimeReportStore = useRealtimeReportStore();

const filters: Ref<RealtimeReportFilter> = ref({
    queues: realtimeReportStore.defaultFilter.queues,
});

watch(filterOpened, () => {
    refreshLocalFilter();
});

const queueOptions = ref<SelectOptionsServiceGetQueuesResult[]>([]);
const fetchQueues = async () => {
    try {
        const { data } = await SelectOptionsService.getQueues();
        queueOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const applyFilters = () => {
    realtimeReportStore.applyFilters(filters.value);
};

const clearFilters = () => {
    realtimeReportStore.clearFilter();
    refreshLocalFilter();
};

const refreshLocalFilter = () => {
    filters.value.queues = realtimeReportStore.filter.queues;
};

onMounted(() => {
    fetchQueues();
});
</script>

<template>
    <UiRightPanel v-model="filterOpened" :title="$t('filter.sidebar.title')">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                @click="filterOpened = true"
            >
                <FilterIcon class="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                {{ $t('core.actions.Filter') }}
            </UiButton>
        </template>
        <template #default>
            <form autocomplete="off" class="flex flex-col gap-3">
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-5">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('report.realtime.filters.queue') }}
                        </label>
                        <UiMultiselect
                            name="realtimeQueueFilter"
                            :options="queueOptions"
                            v-model="filters.queues"
                            label="name"
                            value-prop="id"
                            multiple
                            can-clear
                        />
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-2 place-content-around gap-4">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters()">
                        {{ $t('core.actions.Clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="applyFilters()">
                        {{ $t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>
